import React, { ReactElement } from 'react';
import stringify from '../../../../helpers/encodeUri';

type Profile = {
  width: number;
  height: number;
  rule: string;
};

type Layout = 'responsive' | 'fixed';

export type ContentfulImageProps = {
  src: string;
  className?: string;
  fitType?: string;
  alt: string;
  quality?: number;
  layout?: Layout;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
} & (
  | { profiles: Profile[]; sizes?: string; width?: never; height?: never }
  | { width: number; height: number; sizes?: never; profiles?: never }
);

export const addParamsToContentfulImage = (url: string, params: Record<string, any>): string => {
  if (Object.entries(params).length === 0) {
    return url;
  }

  const queryString = stringify(params);

  return `${url}?${queryString}`;
};

export type ImageParams = {
  width: number;
  height: number;
  quality?: number;
  fit?: string;
  fitType?: string;
};
export const transformImageParams = ({
  width,
  height,
  quality = 90,
  fit = 'fill',
  fitType = 'center',
}: ImageParams) => ({
  w: width,
  h: height,
  q: quality,
  fit,
  f: fitType,
});

export const ContentfulImage = ({
  src,
  className = '',
  width,
  height,
  profiles = [],
  sizes,
  alt,
  layout = 'fixed',
  objectFit,
}: ContentfulImageProps): ReactElement => {
  if (!src) return <></>;

  const profileArray =
    width && height
      ? [
          {
            width,
            height,
            rule: '1x',
          },
          {
            width: width * 2,
            height: height * 2,
            rule: '2x',
          },
        ]
      : profiles;

  const profilesWithContentfulParams = profileArray.map(({ width, height, rule }: Profile) => {
    const imageParams = transformImageParams({ width, height });

    const imageSrc = addParamsToContentfulImage(src, imageParams);
    return { imageSrc, rule };
  });

  const isBitmap = src?.split('.').pop()?.toLowerCase() === 'svg';
  const srcSet = isBitmap
    ? profilesWithContentfulParams
        .map(({ imageSrc, rule }: { imageSrc: string; rule: string }) => `${imageSrc} ${rule}`)
        .join(', ')
    : undefined;

  const dimensions = {
    ...(profiles.length
      ? {
          width: profiles[profiles.length - 1]?.width,
          height: profiles[profiles.length - 1]?.height,
        }
      : { width, height }),
  };

  const style = {
    display: 'block',
    ...(layout === 'responsive'
      ? {
          width: '100%',
          height: '100%',
          ...(objectFit && { objectFit }),
        }
      : undefined),
  };

  return (
    <img
      src={`${profilesWithContentfulParams[0].imageSrc}&fm=webp`}
      srcSet={srcSet}
      className={className}
      sizes={sizes}
      alt={alt}
      style={style}
      {...dimensions}
    />
  );
};
